import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CFormInput,
  CImage,
  CRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilArrowCircleLeft } from '@coreui/icons'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { buildApiUrl } from '../../utils/getConfiguration'
import EmptyStatePanel from '../../components/EmptyStatePanel'
import BadgeComponent from '../widgets/BadgeComponent'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'

const isDevelopment = process.env.NODE_ENV !== 'production'

const Ignoredrisks = () => {
  const [recommendations, setRecommendations] = useState([])
  const [groups, setGroups] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()
  const { t } = useTranslation()

  const fetchRecommendationsData = useCallback(async () => {
    try {
      const account_ID = localStorage.getItem('account_ID')
      const response = await apiClient.get(buildApiUrl('accountRemindersIgnored', { account_ID }))
      setRecommendations(response.data)
    } catch (error) {
      console.error('Error fetching recommendations', error)
    }
  }, [])

  const fetchGroupsData = useCallback(async () => {
    try {
      const response = await apiClient.get(buildApiUrl('aois'))
      setGroups(response.data)
    } catch (error) {
      console.error('Error fetching groups', error)
    }
  }, [])

  useEffect(() => {
    fetchRecommendationsData()
    fetchGroupsData()
  }, [fetchRecommendationsData, fetchGroupsData])

  const groupLookup = useMemo(() => {
    const map = new Map()
    groups.forEach((group) => {
      const key = Number(group?.aoi_ID ?? group?.grp_ID ?? group?.id)
      if (!Number.isNaN(key)) {
        map.set(key, group?.Title ?? group?.title ?? '')
      }
    })
    return map
  }, [groups])

  const filteredRecommendations = useMemo(() => {
    if (!searchTerm) return recommendations
    const term = searchTerm.trim().toLowerCase()
    return recommendations.filter((item) =>
      String(item?.Title ?? '')
        .toLowerCase()
        .includes(term),
    )
  }, [recommendations, searchTerm])

  const handleClickActive = async () => {
    try {
      if (isDevelopment) {
        console.debug('Account_ID ', localStorage.getItem('account_ID'))
        console.debug('Reminder_ID ', localStorage.getItem('taskid'))
      }
      const reminder_ID = localStorage.getItem('taskid')
      const account_ID = localStorage.getItem('account_ID')
      await apiClient.get(
        buildApiUrl('remindersStatusUpdate', {
          reminder_ID,
          state: 'A',
          account_ID,
        }),
      )
      await fetchRecommendationsData()
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader
          title={t('ignoredrisks.header.title')}
          onBack={() => navigate('/dashboard')}
        />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <CCard className="border-0 shadow-sm">
              <CCardBody className="p-4 p-lg-5 d-flex flex-column gap-4">
                <CFormInput
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('ignoredrisks.search.placeholder')}
                  aria-label={t('ignoredrisks.search.aria')}
                />

                <CRow className="g-3">
                  {filteredRecommendations.length > 0 ? (
                    filteredRecommendations.map((recommendation) => (
                      <CCol xs={12} key={recommendation.reminder_ID}>
                        <CCard className="border-0 shadow-sm">
                          <CCardBody>
                            <CRow className="g-4 align-items-center">
                              <CCol
                                xs={12}
                                md={4}
                                lg={3}
                                className="d-flex align-items-center gap-3"
                              >
                                <CImage
                                  rounded
                                  orientation="top"
                                  loading="lazy"
                                  src={recommendation.pic}
                                  width={60}
                                  height={60}
                                  alt={recommendation.Title || t('ignoredrisks.reminderAlt')}
                                />
                                <div className="d-flex flex-column gap-2">
                                  <BadgeComponent priority={recommendation.Prio} />
                                  <span className="text-medium-emphasis small">
                                    {groupLookup.get(Number(recommendation.grp_ID)) ||
                                      t('ignoredrisks.unknownArea')}
                                  </span>
                                </div>
                              </CCol>
                              <CCol xs={12} md={5} lg={6}>
                                <div className="fw-semibold mb-1">{recommendation.Title}</div>
                                {recommendation.Description && (
                                  <div className="text-medium-emphasis small">
                                    {recommendation.Description}
                                  </div>
                                )}
                              </CCol>
                              <CCol xs={12} md={3} lg={3} className="d-flex justify-content-md-end">
                                <CButton
                                  color="info"
                                  aria-label={t('ignoredrisks.actions.returnToActive')}
                                  onClick={() => {
                                    localStorage.setItem('taskid', recommendation.reminder_ID)
                                    handleClickActive()
                                  }}
                                >
                                  <CIcon icon={cilArrowCircleLeft} size="lg" />
                                </CButton>
                              </CCol>
                            </CRow>
                          </CCardBody>
                        </CCard>
                      </CCol>
                    ))
                  ) : (
                    <CCol xs={12}>
                      <EmptyStatePanel
                        icon={cilArrowCircleLeft}
                        title={
                          recommendations.length === 0
                            ? t('ignoredrisks.empty.noIgnoredTitle')
                            : t('ignoredrisks.empty.noResultsTitle')
                        }
                        message={
                          recommendations.length === 0
                            ? t('ignoredrisks.empty.noIgnoredMessage')
                            : t('ignoredrisks.empty.noResultsMessage')
                        }
                      />
                    </CCol>
                  )}
                </CRow>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Ignoredrisks
